// The analytics design tokens used by startup-web's Performance and Profile
// Views screens, ported here so the admin dashboard reads as the same product
// rather than a different one. Values are copied from
// startup-web/src/views/performance/components/{MetricSummaryCard,MetricChartCard,TrendIndicator}.js
// — change them there and here together.

export const T = {
  // Ink
  ink: "#101828",
  inkMuted: "#344054",
  inkSubtle: "#667085",
  inkFaint: "#98A2B3",

  // Surfaces and lines
  border: "#EAECF0",
  grid: "#F2F4F7",
  pill: "#F2F4F7",
  surface: "#FFFFFF",
  // The page behind the cards (ThemeBGWrapper's default background). Needed
  // by anything that sticks: a transparent sticky header lets rows show
  // through as they scroll under it.
  pageBg: "#fffdfa",
  accentSoft: "#F9F5FF",
  iconChip: "#F4EBFF",

  // Brand
  accent: "#6938EF",

  // Status. startup-web DOES carry a green, which admin-web's own theme does
  // not — so a positive delta can read as positive here, instead of the
  // neutral gray this dashboard used before adopting these tokens.
  up: "#12B76A",
  down: "#F04438",
  flat: "#667085",

  // Text-weight variants of those two. As TEXT on white, T.up is 2.2:1 and
  // T.down 3.3:1 — both below WCAG AA's 4.5:1 for the 0.72rem delta label.
  // These read as the same red and green and pass; the originals stay for
  // fills and strokes, where the contrast rule does not apply.
  upText: "#027A48",
  downText: "#B42318",
  upBg: "#ECFDF3",
  downBg: "#FEF3F2",
  flatBg: "#F2F4F7",

  radius: "12px",
};

// Every chart on this page animates when its data changes, so a range switch
// reads as the same bars moving rather than a different page appearing.
//
// Width and opacity only — never layout properties — so the browser can run
// these off the main thread, and nothing animates for anyone who asked their
// system not to.
export const MOTION = {
  chart: "420ms cubic-bezier(0.4, 0, 0.2, 1)",
  sx: {
    transition: "width 420ms cubic-bezier(0.4, 0, 0.2, 1)",
    "@media (prefers-reduced-motion: reduce)": { transition: "none" },
  },
};

// Alternating series colours, exactly as startup-web assigns them:
// CHART_COLORS[index % CHART_COLORS.length], one colour per CARD.
//
// Validated, and the result constrains how they may be used:
//   as 2 series in ONE chart  -> FAIL (CVD ΔE 1.9 deutan, normal-vision ΔE 9.4,
//                                both below floor — nearly indistinguishable)
//   as 1 series per panel     -> fine; colour is not carrying identity, the
//                                panel's own title is
//
// So never put these two in the same plot. Our daily chart is small multiples —
// one series per panel — which is why it can use them.
export const SERIES = ["#7A5AF8", "#3E7BFA"];

// Soft row tints for the ranked breakdown lists, matched to SERIES.
//
// These are TINTS, drawn behind a row's text. They are not fills: #F4EBFF on a
// T.grid (#F2F4F7) track differs by about 1.1:1, so a bar painted in one
// disappears into its own track. A nested bar wants ACCENT_NESTED instead.
export const SERIES_SOFT = ["#F4EBFF", "#EFF4FF"];

// One lightness step down from T.accent, for a bar nested under another bar —
// light enough to read as the child level, dark enough to stay visible as a
// FILL on T.grid. Same hue and the same stepping idea as PIPELINE_COLORS.
export const ACCENT_NESTED = "#9E77ED";

// The pipeline bar's four segments. One hue, stepped by LIGHTNESS, so they stay
// separable under every colour-vision deficiency — the one rule that matters
// when four categories share a single stacked bar. Rejected leaves the hue
// entirely: it is not a stage on the way to invested, it is the exit.
export const PIPELINE_COLORS = {
  invited: "#E9D7FE",
  interested: "#9E77ED",
  invested: "#5925DC",
  rejected: "#D0D5DD",
};

// Profile completion bands, lowest to highest. ORDINAL, so one hue stepped by
// lightness - the pipeline's purples, extended to five steps - and never a
// rainbow. Validated as an ordinal ramp on white: monotone lightness, every
// adjacent step >= 0.06 apart, light end 2.09:1, hue spread 15deg. The rows
// beside the bar carry the label and count, so identity is never colour alone.
export const COMPLETION_COLORS = {
  below_25: "#C3A5F9",
  "25_to_50": "#9E77ED",
  "50_to_75": "#7A4FE0",
  above_75: "#5925DC",
  complete: "#3E1C96",
};

// Chart chrome: axis lines and ticks hidden, only recessive horizontal grid.
export const chartChromeSx = {
  "& .MuiChartsAxis-line, & .MuiChartsAxis-tick": { display: "none" },
  "& .MuiChartsGrid-line": { stroke: T.grid },
  "& .MuiChartsAxis-tickLabel": { fontSize: "0.65rem", fill: T.inkFaint },
};

// "▲ 8.4%" pill, same shape startup-web uses in its chart card headers.
export const trendPillSx = (direction) => ({
  display: "inline-flex",
  alignItems: "center",
  gap: "2px",
  borderRadius: "6px",
  padding: "1px 6px",
  fontSize: "0.72rem",
  fontWeight: 600,
  whiteSpace: "nowrap",
  color: direction === "up" ? T.upText : direction === "down" ? T.downText : T.flat,
  backgroundColor: direction === "up" ? T.upBg : direction === "down" ? T.downBg : T.flatBg,
});

export const cardSx = {
  boxShadow: "none",
  borderRadius: T.radius,
  border: `1px solid ${T.border}`,
  background: T.surface,
};
